/**
 * What the vocabulary words mean to somebody who is not us.
 *
 * The API speaks in stable identifiers so that storing them is safe; a card
 * has to speak English. Anything unrecognised falls through to the identifier
 * with its underscores removed, so a word added to the backend shows up
 * readable rather than blank while this file catches up.
 */
const STAGES: Record<string, string> = {
  idea: 'Just an idea',
  validating: 'Talking to people',
  prototype: 'Prototype',
  launched: 'Launched',
};

const COMPENSATION: Record<string, string> = {
  equity: 'Equity',
  paid: 'Paid',
  hybrid: 'Equity and pay',
  portfolio: 'For the portfolio',
};

const AVAILABILITY: Record<string, string> = {
  weekends: 'Weekends',
  part_time: 'Part time',
  full_time: 'Full time',
};

const COLLABORATION: Record<string, string> = {
  cofounder_equity: 'Cofounder, for equity',
  paid_freelance: 'Paid freelance',
  portfolio: 'For the portfolio',
  open: 'Open to anything',
};

function readable(value: string): string {
  return value.replace(/_/g, ' ');
}

export const availabilityLabel = (value: string): string => AVAILABILITY[value] ?? readable(value);
export const collaborationLabel = (value: string): string =>
  COLLABORATION[value] ?? readable(value);

export const stageLabel = (value: string): string => STAGES[value] ?? readable(value);
export const compensationLabel = (value: string): string => COMPENSATION[value] ?? readable(value);

/** "2 days ago", near enough. Precision here is noise: it is a judgement cue. */
export function agoLabel(iso: string, verb: string): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days <= 0) return `${verb} today`;
  if (days === 1) return `${verb} yesterday`;
  if (days < 30) return `${verb} ${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? `${verb} a month ago` : `${verb} ${months} months ago`;
}

export const postedLabel = (iso: string): string => agoLabel(iso, 'Posted');
export const likedLabel = (iso: string): string => agoLabel(iso, 'Liked it');
export const matchedLabel = (iso: string): string => agoLabel(iso, 'Matched');
